import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { getApiAsync } from "../../../../api";
import { AuthorPublisher } from "../../../../api/graphql";

type AuthorPublisherPanelProps = { parentId: string };

export const AuthorPublisherPanel: React.FC<AuthorPublisherPanelProps> = ({
  parentId,
}) => {
  const rootStore = useRootStore();
  // Extraídos del rootStore para que ESLint vea la cadena de deps del useEffect.
  const { langStore, authStore, activityStore } = rootStore;
  const [items, setItems] = useState<AuthorPublisher[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      try {
        if (cancelled) return;
        const api = await getApiAsync(authStore, activityStore);
        const result = await api.authorPublisherListWithOffset({
          where: {
            parent_AuthorComposer_publishers: { id: { equals: parentId } },
          },

          take: 100,
          offset: 0,
        });
        if (!cancelled) {
          setItems(result.authorPublisherListWithOffset as AuthorPublisher[]);
        }
      } catch (error) {
        console.error("Failed to load authorPublisher:", error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, [parentId, authStore, activityStore]);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
      >
        {langStore.AuthorPublisherLabels?.p || "AuthorPublisher"}
        {loading && <CircularProgress size={16} />}
        {!loading && items.length === 0 && (
          <Typography component="span" variant="body2" color="text.secondary">
            ({langStore.noRecordsMessage})
          </Typography>
        )}
      </Typography>
      {!loading && items.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                {langStore.AuthorPublisherPublisherLabel || "publisher"}
              </TableCell>
              <TableCell>
                {langStore.AuthorPublisherPercentageLabel || "percentage"}
              </TableCell>
              <TableCell>
                {langStore.AuthorPublisherDurationInYearsLabel ||
                  "durationInYears"}
              </TableCell>
              <TableCell>
                {langStore.AuthorPublisherAutoRenewalYearsLabel ||
                  "autoRenewalYears"}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <React.Fragment key={item.id}>
                <TableRow
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    rootStore.openDrawerView("authorPublisher", item.id);
                  }}
                >
                  <TableCell>{item.publisher?.displayId || "-"}</TableCell>
                  <TableCell>{String(item.percentage ?? "-")}</TableCell>
                  <TableCell>{String(item.durationInYears ?? "-")}</TableCell>
                  <TableCell>{String(item.autoRenewalYears ?? "-")}</TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};
